<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import {
  type AdminRobot,
  EMPTY_NEW_ADMIN_ROBOT,
  type NewAdminRobot,
  StatusEnum,
} from '@/models/AdminRobot';
import {
  createAdminRobot,
  loadAdminRobots,
  patchAdminRobot,
  deleteAdminRobot,
  getRobotPassword,
} from '@/api/AdminRobotApi';
import type { Cell } from '@/models/Cell';
import { loadCells } from '@/api/AdminCellApi';
import formatDate from '@/helpers/DateFormatterHelper';
import { getColorCssValue } from '@/utils';
import {
  requiredRule,
  selectFieldRequiredRule,
  serialNumberRegexRule,
} from '@/helpers/FormRules';
import useAuthStore from '@/store/AuthStore';
import type HealthIndicatorEnum from '@/enums/HealthIndicatorEnum';
import { healthColor } from '@/helpers/HealthIndicatorHelper';
import { Permission } from '@/models/Roles';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();

const robots: Ref<AdminRobot[]> = ref([]);
const cells: Ref<Cell[]> = ref([]);
const error = ref('');
const createError = ref('');
const patchError = ref('');
const deleteError = ref('');
const passwordError = ref('');
const robotPassword = ref('');
const selectedRobot: Ref<AdminRobot | null> = ref(null);
const editedRobot: Ref<AdminRobot | null> = ref(null);
const robotToDelete: Ref<AdminRobot | null> = ref(null);
const passwordRobot: Ref<AdminRobot | null> = ref(null);
const detailDialog = ref(false);
const editDialog = ref(false);
const isEditing = ref(false);
const createDialog = ref(false);
const deleteDialog = ref(false);
const passwordDialog = ref(false);
const searchQuery = ref('');
const iconColor = ref('gray');
const copiedMessage = ref('');
const newRobot: Ref<NewAdminRobot> = ref(cloneDeep(EMPTY_NEW_ADMIN_ROBOT));

const createControlUnitValid = ref(false);
const editControlUnitValid = ref(true);

function getHealthColor(healthIndicator: HealthIndicatorEnum) {
  return healthColor(healthIndicator);
}

async function getRobots() {
  error.value = '';
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    robots.value = await loadAdminRobots();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    error.value = err;
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

async function getCells() {
  if (!authStore.hasPermission(Permission.GET_CUSTOMER)) {
    return;
  }
  try {
    cells.value = await loadCells();
  } catch (err) {
    console.error(err);
  }
}

async function createRobot(robot: NewAdminRobot) {
  try {
    createError.value = '';
    await createAdminRobot(robot);
    getRobots();
    createDialog.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
    createError.value = err;
  }
}

function closeEditDialog() {
  patchError.value = '';
  editedRobot.value = null;
  editDialog.value = false;
  isEditing.value = false;
}

async function saveChanges() {
  if (editedRobot.value) {
    try {
      patchError.value = '';
      await patchAdminRobot(editedRobot.value);
      getRobots();
      closeEditDialog();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      patchError.value = err;
    }
  }
}

function openDetailDialog(robot: AdminRobot) {
  if (!isEditing.value) {
    selectedRobot.value = robot;
    detailDialog.value = true;
  }

  editedRobot.value = cloneDeep(robot);
}

function openCreateDialog() {
  createDialog.value = true;
}

function enableEdit(robot: AdminRobot | null) {
  selectedRobot.value = robot;
  editedRobot.value = cloneDeep(robot);
  detailDialog.value = false;
  editDialog.value = true;
  isEditing.value = true;
}

function closeDetailDialog() {
  detailDialog.value = false;
  selectedRobot.value = null;
}

function closeCreateDialog() {
  createDialog.value = false;
  createError.value = '';
}

async function enableDelete(robot: AdminRobot | null) {
  robotToDelete.value = robot;
  deleteDialog.value = true;
}

function closeDeleteDialog() {
  deleteDialog.value = false;
  robotToDelete.value = null;
  deleteError.value = '';
}

async function confirmDelete() {
  if (robotToDelete.value) {
    try {
      deleteError.value = '';
      await deleteAdminRobot(robotToDelete.value);
      getRobots();
      closeDeleteDialog();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);

      deleteError.value = err;
    }
  }
}

async function fetchRobotPassword() {
  if (passwordRobot.value) {
    loadingSpinnerStore.setShowLoadingOverlay(true);
    try {
      passwordError.value = '';
      robotPassword.value = await getRobotPassword(passwordRobot.value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      passwordError.value = err;
    } finally {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  }
}

async function openPasswordDialog(robot: AdminRobot | null) {
  if (!robot) {
    return;
  }
  passwordRobot.value = robot;
  await fetchRobotPassword();
  passwordDialog.value = true;
}

function closePasswordDialog() {
  passwordDialog.value = false;
  passwordRobot.value = null;
  passwordError.value = '';
}

const filteredRobots = computed(() => {
  const query = (searchQuery.value || '').toLowerCase();
  return robots.value.filter((robot) => {
    const nameMatch = robot.name.toLowerCase().includes(query);
    const ipMatch = robot.ipAddress?.toLowerCase().includes(query);
    const serialNumberMatch = robot.serialNumber.toLowerCase().includes(query);
    const cellMatch = robot.cellName?.toLowerCase().includes(query);
    const customerMatch = robot.customerName?.toLowerCase().includes(query);
    const internalNameMatch = robot.internalName?.toLowerCase().includes(query);

    return (
      nameMatch ||
      ipMatch ||
      serialNumberMatch ||
      cellMatch ||
      customerMatch ||
      internalNameMatch
    );
  });
});

function cellFilter(
  _itemTitle: string,
  query: string,
  item?: { raw: Cell },
): boolean {
  if (!query) {
    return true;
  }
  if (!item) {
    return false;
  }
  const q = query.toLowerCase();
  return (
    (item.raw.name || '').toLowerCase().includes(q) ||
    (item.raw.customerName || '').toLowerCase().includes(q)
  );
}

function getStatusColor(status: StatusEnum) {
  if (status === StatusEnum.OKAY) {
    return getColorCssValue('success');
  }
  if (status === StatusEnum.NOT_OKAY) {
    return getColorCssValue('error');
  }
  if (status === StatusEnum.DEPLOYING) {
    return getColorCssValue('info');
  }
  return '';
}

const headers = computed(() => {
  const baseHeaders: object[] = [
    { title: $gettext('Name'), value: 'name', sortable: true },
    {
      title: $gettext('Serial number'),
      value: 'serialNumber',
      sortable: true,
    },
    {
      title: $gettext('Cell'),
      value: 'cellName',
      sortable: true,
    },
    {
      title: $gettext('Customer'),
      value: 'customerName',
      sortable: true,
    },
    {
      title: $gettext('Internal Name'),
      value: 'internalName',
      sortable: true,
    },
    {
      title: $gettext('Status'),
      value: 'status',
      sortable: true,
    },
    {
      title: $gettext('Online'),
      value: 'isOnline',
      sortable: true,
    },
  ];

  if (authStore.hasPermission(Permission.GET_CONTROL_UNIT_PASSWORD)) {
    baseHeaders.push({
      title: $gettext('Password'),
      key: 'password',
      sortable: false,
      width: '100px',
    });
  }

  if (authStore.hasPermission(Permission.PATCH_CONTROL_UNIT)) {
    baseHeaders.push({
      title: $gettext('Edit'),
      key: 'edit',
      sortable: false,
      width: '100px',
    });
  }

  if (authStore.hasPermission(Permission.DELETE_CONTROL_UNIT)) {
    baseHeaders.push({
      title: $gettext('Delete'),
      key: 'delete',
      sortable: false,
      width: '100px',
    });
  }

  return baseHeaders;
});

function copyToClipboard(text: string) {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      iconColor.value = 'green';
      copiedMessage.value = $gettext('Copied!');
      setTimeout(() => {
        iconColor.value = 'grey';
        copiedMessage.value = '';
      }, 1000);
    })
    .catch((err) => {
      console.error('Error copying text to clipboard:', err);
    });
}

onMounted(() => {
  getRobots();
  getCells();
});
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Robots') }}
          <v-btn
            size="small"
            class="mb-1 ml-4 gradient-oceanblue-whitesands"
            icon="mdi-cached"
            @click="getRobots"
          />
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-4">
        <v-row
          v-if="authStore.hasPermission(Permission.POST_CONTROL_UNIT)"
          class="ma-4 ml-0"
        >
          <r-btn variant="outlined" @click="openCreateDialog">{{
            $gettext('Add Robot')
          }}</r-btn>
        </v-row>
        <v-row class="mt-12">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-text-field
              v-model="searchQuery"
              label="Search"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="error" class="mt-8">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-alert
              class="mt-8"
              border="top"
              icon="mdi-close-circle"
              border-color="error"
            >
              {{ error }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              items-per-page="25"
              :items="filteredRobots"
              :headers="headers"
              @click:row="
                (event: Event, item: any) => openDetailDialog(item.item)
              "
            >
              <template #item.status="{ item }">
                <span :style="{ color: getStatusColor(item.status) }">{{
                  item.status
                }}</span>
              </template>
              <template #item.isOnline="{ item }">
                <v-icon
                  icon="mdi-circle"
                  :color="
                    item.isOnline === true
                      ? getColorCssValue('success')
                      : getColorCssValue('error')
                  "
                />
              </template>
              <template #item.password="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-form-textbox-password"
                        @click.stop="openPasswordDialog(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Password') }}
                </v-tooltip>
              </template>
              <template #item.edit="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-pencil"
                        @click.stop="enableEdit(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Edit') }}
                </v-tooltip>
              </template>
              <template #item.delete="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-trash-can-outline"
                        @click.stop="enableDelete(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Delete') }}
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-model="createDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $gettext('Add Robot') }}</v-card-title>
        <v-card-text>
          <v-form v-model="createControlUnitValid">
            <v-text-field
              v-model="newRobot.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="newRobot.serialNumber"
              :label="$gettext('Serial number')"
              :rules="[requiredRule, serialNumberRegexRule]"
            ></v-text-field>
            <v-text-field
              v-model="newRobot.internalName"
              :label="$gettext('Internal Name')"
            ></v-text-field>
            <v-autocomplete
              v-model="newRobot.cellUuid"
              :items="cells"
              item-title="name"
              item-value="uuid"
              :label="$gettext('Cell')"
              :custom-filter="cellFilter"
              clearable
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item
                  v-bind="itemProps"
                  :title="item.raw.name"
                  :subtitle="item.raw.customerName"
                />
              </template>
            </v-autocomplete>
            <v-select
              v-model="newRobot.status"
              :items="Object.values(StatusEnum)"
              :label="$gettext('Status')"
              :rules="[selectFieldRequiredRule]"
            ></v-select>
            <v-textarea
              v-model="newRobot.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
          <v-alert v-if="createError" class="mx-4" type="error" outlined>
            {{ createError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeCreateDialog()">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!createControlUnitValid"
            @click="createRobot(newRobot)"
            >{{ $gettext('Add') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="selectedRobot"
      v-model="detailDialog"
      max-width="600"
      @click:outside="closeDetailDialog"
    >
      <v-card>
        <v-card-title>{{ $gettext('Robot') }}</v-card-title>
        <v-card-text>
          <div>
            <p>
              {{ $gettext('Name') }}:
              <span class="font-weight-bold">{{ selectedRobot.name }}</span>
            </p>
            <p>
              {{ $gettext('Serial number') }}:
              <span class="font-weight-bold">{{
                selectedRobot.serialNumber
              }}</span>
            </p>
            <p>
              {{ $gettext('Internal Name') }}:
              <span class="font-weight-bold">{{
                selectedRobot.internalName
              }}</span>
            </p>
            <p>
              {{ $gettext('Cell') }}:
              <span class="font-weight-bold">{{ selectedRobot.cellName }}</span>
            </p>
            <p>
              {{ $gettext('Customer') }}:
              <span class="font-weight-bold">{{
                selectedRobot.customerName
              }}</span>
            </p>
            <p>
              {{ $gettext('Status') }}:
              <span class="font-weight-bold">{{ selectedRobot.status }}</span>
            </p>
            <p>
              {{ $gettext('Internal Notes') }}:
              <span class="font-weight-bold">{{
                selectedRobot.internalNotes
              }}</span>
            </p>
            <p>
              {{ $gettext('IP Address') }}:
              <span
                v-if="selectedRobot.ipAddress"
                class="font-weight-bold"
                style="cursor: pointer; user-select: none"
                :title="$gettext('Click to copy IP Address')"
                @click="
                  selectedRobot?.ipAddress &&
                  copyToClipboard(selectedRobot.ipAddress)
                "
              >
                {{ selectedRobot.ipAddress }}
                <v-icon :color="iconColor">mdi-content-copy</v-icon>
                <span v-if="copiedMessage">{{ copiedMessage }}</span>
              </span>
            </p>
            <p>
              {{ $gettext('Is Online') }}:
              <span class="font-weight-bold">{{
                selectedRobot.isOnline ? 'online' : 'offline'
              }}</span>
            </p>
            <p>
              {{ $gettext('Last Seen') }}:
              <span class="font-weight-bold">{{
                formatDate(selectedRobot.lastSeen, true)
              }}</span>
            </p>
            <p>
              {{ $gettext('Health status') }}:
              <span class="font-weight-bold">
                <v-icon
                  class="mr-2"
                  icon="mdi-alarm-light"
                  :color="getHealthColor(selectedRobot.overallHealthIndicator)"
                />
              </span>
            </p>
            <p>
              {{ $gettext('Show in Grafana') }}:
              <span class="font-weight-bold"
                ><a
                  v-bind:href="selectedRobot.grafanaLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  >Link</a
                ></span
              >
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeDetailDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            v-if="authStore.hasPermission(Permission.PATCH_CONTROL_UNIT)"
            color="primary"
            @click="enableEdit(selectedRobot)"
            >{{ $gettext('Edit') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="editedRobot"
      v-model="editDialog"
      max-width="600"
      @click:outside="closeEditDialog"
    >
      <v-card>
        <v-card-title>{{ $gettext('Robot') }}</v-card-title>
        <v-card-text>
          <v-form v-model="editControlUnitValid">
            <v-text-field
              v-model="editedRobot.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="editedRobot.serialNumber"
              :label="$gettext('Serial number')"
              :rules="[requiredRule, serialNumberRegexRule]"
            ></v-text-field>
            <v-text-field
              v-model="editedRobot.internalName"
              :label="$gettext('Internal Name')"
            ></v-text-field>
            <v-autocomplete
              v-model="editedRobot.cellUuid"
              :items="cells"
              item-title="name"
              item-value="uuid"
              :label="$gettext('Cell')"
              :custom-filter="cellFilter"
              clearable
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item
                  v-bind="itemProps"
                  :title="item.raw.name"
                  :subtitle="item.raw.customerName"
                />
              </template>
            </v-autocomplete>
            <v-select
              v-model="editedRobot.status"
              :items="Object.values(StatusEnum)"
              :label="$gettext('Status')"
              :rules="[selectFieldRequiredRule]"
            ></v-select>
            <v-textarea
              v-model="editedRobot.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
          <v-alert v-if="patchError" class="mx-4" type="error" outlined>
            {{ patchError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeEditDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!editControlUnitValid"
            @click="saveChanges"
            >{{ $gettext('Save') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ $gettext('Confirm Deletion') }}
        </v-card-title>
        <v-card-text>
          <p>
            {{ $gettext('Are you sure you want to delete this control unit?') }}
          </p>
          <p>
            <strong>{{ robotToDelete?.name }}</strong>
          </p>
          <p>
            <strong>{{ robotToDelete?.serialNumber }}</strong>
          </p>
          <v-alert v-if="deleteError" class="mx-2" type="error">{{
            deleteError
          }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="closeDeleteDialog">{{
            $gettext('Cancel')
          }}</v-btn>
          <v-spacer />
          <v-btn color="error" variant="text" @click="confirmDelete">{{
            $gettext('Delete')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="passwordDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ passwordRobot?.serialNumber }} {{ $gettext('Password') }}
        </v-card-title>
        <v-card-text>
          <h2>{{ robotPassword }}</h2>
          <v-alert v-if="passwordError" class="mx-2" type="error">{{
            deleteError
          }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closePasswordDialog">{{
            $gettext('Close')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.clickable-item:hover {
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
